import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateTime } from "@/lib/helpers";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";

const COLORS = ["#2563eb", "#0d9488", "#f59e0b", "#f43f5e", "#10b981"];

// Module-level constants (not recreated every render) — passed as-is into
// recharts axis/bar props below.
const CHART_AXIS_TICK = { fontSize: 12 };
const CHART_BAR_RADIUS = [6, 6, 0, 0];

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [instructorHours, setInstructorHours] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [noShows, setNoShows] = useState([]);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [paymentsReport, setPaymentsReport] = useState(null);
  const [balances, setBalances] = useState([]);
  const [maintenance, setMaintenance] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [hoursRes, attRes, noShowRes, completedRes, upcomingRes, payRes, balRes, maintRes] = await Promise.all([
          api.get("/reports/instructor-hours"),
          api.get("/reports/attendance"),
          api.get("/reports/no-shows"),
          api.get("/reports/lessons", { params: { status: "completed" } }),
          api.get("/reports/lessons", { params: { status: "scheduled" } }),
          api.get("/reports/payments"),
          api.get("/reports/outstanding-balances"),
          api.get("/reports/vehicle-maintenance"),
        ]);
        setInstructorHours(hoursRes.data);
        setAttendance(attRes.data);
        setNoShows(noShowRes.data);
        setCompletedLessons(completedRes.data);
        setUpcomingLessons(upcomingRes.data);
        setPaymentsReport(payRes.data);
        setBalances(balRes.data);
        setMaintenance(maintRes.data);
      } catch {
        toast.error("Could not load reports");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const attendanceData = useMemo(
    () => Object.entries(attendance).map(([key, value]) => ({ name: key.replace("_", " "), value })),
    [attendance]
  );

  if (loading) return <Skeleton className="h-96 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-5" data-testid="reports-page">
      <Tabs defaultValue="hours">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="hours" data-testid="reports-tab-hours">Instructor Hours</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="reports-tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="no-shows" data-testid="reports-tab-no-shows">No-Shows</TabsTrigger>
          <TabsTrigger value="lessons" data-testid="reports-tab-lessons">Lessons</TabsTrigger>
          <TabsTrigger value="payments" data-testid="reports-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="balances" data-testid="reports-tab-balances">Balances</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="reports-tab-maintenance">Maintenance</TabsTrigger>
        </TabsList>

        <TabsContent value="hours" className="pt-5">
          <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5" style={{ height: 340 }}>
            {instructorHours.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center pt-16">No time entries recorded yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={instructorHours}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="user_name" tick={CHART_AXIS_TICK} />
                  <YAxis tick={CHART_AXIS_TICK} />
                  <Tooltip />
                  <Bar dataKey="total_hours" fill="#2563eb" radius={CHART_BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </TabsContent>

        <TabsContent value="attendance" className="pt-5">
          <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 flex items-center justify-center" style={{ height: 340 }}>
            {attendanceData.length === 0 ? (
              <p className="text-sm text-muted-foreground">No lesson data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={attendanceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label>
                    {attendanceData.map((entry, idx) => (
                      <Cell key={entry.name} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </TabsContent>

        <TabsContent value="no-shows" className="pt-5 space-y-2">
          {noShows.length === 0 ? (
            <p className="text-sm text-muted-foreground rounded-[var(--radius)] border bg-card p-8 text-center">No no-shows recorded.</p>
          ) : (
            <div className="rounded-[var(--radius)] border bg-card divide-y">
              {noShows.map((a) => (
                <div key={a.id} className="flex items-center justify-between p-4" data-testid="report-row">
                  <span className="text-sm">{a.student_name} · {a.instructor_name}</span>
                  <span className="text-xs text-muted-foreground">{formatDateTime(a.start_time)}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="lessons" className="pt-5 space-y-6">
          <div>
            <h3 className="font-semibold mb-2">Completed Lessons ({completedLessons.length})</h3>
            <div className="rounded-[var(--radius)] border bg-card divide-y max-h-60 overflow-y-auto">
              {completedLessons.slice(0, 30).map((a) => (
                <div key={a.id} className="flex items-center justify-between p-3" data-testid="report-row">
                  <span className="text-sm">{a.student_name}</span>
                  <span className="text-xs text-muted-foreground">{formatDateTime(a.start_time)}</span>
                </div>
              ))}
              {completedLessons.length === 0 && <p className="text-sm text-muted-foreground p-4">None yet.</p>}
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Upcoming Lessons ({upcomingLessons.length})</h3>
            <div className="rounded-[var(--radius)] border bg-card divide-y max-h-60 overflow-y-auto">
              {upcomingLessons.slice(0, 30).map((a) => (
                <div key={a.id} className="flex items-center justify-between p-3" data-testid="report-row">
                  <span className="text-sm">{a.student_name}</span>
                  <span className="text-xs text-muted-foreground">{formatDateTime(a.start_time)}</span>
                </div>
              ))}
              {upcomingLessons.length === 0 && <p className="text-sm text-muted-foreground p-4">None yet.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="pt-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-[var(--radius)] border bg-card p-5">
              <p className="text-xs text-muted-foreground">Total Collected</p>
              <p className="text-2xl font-semibold font-heading text-emerald-600">${paymentsReport?.total_collected.toFixed(2)}</p>
            </div>
            <div className="rounded-[var(--radius)] border bg-card p-5">
              <p className="text-xs text-muted-foreground">Total Refunded</p>
              <p className="text-2xl font-semibold font-heading text-destructive">${paymentsReport?.total_refunded.toFixed(2)}</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="balances" className="pt-5">
          {balances.length === 0 ? (
            <p className="text-sm text-muted-foreground rounded-[var(--radius)] border bg-card p-8 text-center">
              No outstanding balances — every account is current. Great work staying on top of collections.
            </p>
          ) : (
            <div className="rounded-[var(--radius)] border bg-card divide-y">
              {balances.map((b) => (
                <div key={b.student_id} className="flex items-center justify-between p-4" data-testid="report-row">
                  <span className="text-sm font-medium">{b.student_name}</span>
                  <span className={`text-sm font-semibold ${b.balance > 0 ? "text-destructive" : "text-emerald-600"}`}>${b.balance.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="maintenance" className="pt-5">
          {maintenance.length === 0 ? (
            <p className="text-sm text-muted-foreground rounded-[var(--radius)] border bg-card p-8 text-center">All vehicles are up to date.</p>
          ) : (
            <div className="rounded-[var(--radius)] border bg-card divide-y">
              {maintenance.map((m) => (
                <div key={m.vehicle_id} className="p-4 space-y-1" data-testid="report-row">
                  <p className="text-sm font-medium">{m.make} {m.model} ({m.license_plate})</p>
                  <div className="flex flex-wrap gap-1">
                    {m.flags.map((f) => (
                      <StatusBadge key={f} status="maintenance" className="text-xs" />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">{m.flags.join(", ")}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
